import { BrokerInactiveNotice } from '@/features/brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import {
  getProductConfig,
  type QuoteWizardFlow,
} from '@/app/config/products.config';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useCopyQuote } from '@/features/quotes/hooks/common/use-copy-quote.hook';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import { CloneQuoteDialog } from '@/features/quotes/components/clone-quote.dialog';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { useRequirePermissions } from '@/shared/hooks/use-require-permissions.hook';
import { Page, PageHeader } from '@/shared/ui/page';
import { toaster } from '@/shared/ui/toaster.component';
import { isClonedQuoteMarkedAsDraft } from '@/shared/utils/clone-quote-status.util';
import { Button } from '@noorden-insurtech/alba-design-system';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Copy, Home } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NewCapitalGlobalForm } from './new-capital-global-form.component';
import type { BrokerProductCategory } from '@/services/brokers/types';

type Props = {
  productId: string;
  category: string;
  productCode: string;
  stepCode: string;
  quoteFlow?: QuoteWizardFlow;
};

export function NewCapitalGlobalLayout({
  productId,
  category,
  productCode,
  stepCode,
  quoteFlow = 'new',
}: Props) {
  const { proposalNumber, setParams, policyId, id } = useQuoteParams();
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const resolvedPolicyId = (policyId || id || '').trim();
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const copyQuoteMutation = useCopyQuote();
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const { hasPermission: canWriteQuote } = useRequirePermissions({
    permissions: ['policies.quotes.write'],
  });
  const { data: proposalDetails } = useProposalDetails(
    resolvedPolicyId || undefined
  );

  useEffect(() => {
    setParams({
      productId,
      // `category` chega como string crua do search da rota; o contexto guarda
      // a união de BrokerProductCategory.
      category: category as BrokerProductCategory,
      productCode,
      ...(params.id && {
        id: params.id as string,
        policyId: params.id as string,
      }),
    });
  }, [productId, category, productCode, params.id, setParams]);

  /** Nº da proposta (InsureMO) — ao carregar detalhes da cotação existente */
  useEffect(() => {
    const no = (
      proposalDetails?.quotationNo || proposalDetails?.proposalNo
    )?.trim();
    if (no && no !== proposalNumber) {
      setParams({ proposalNumber: no });
    }
  }, [
    proposalDetails?.quotationNo,
    proposalDetails?.proposalNo,
    proposalNumber,
    setParams,
  ]);

  const displayName = getProductConfig(productCode)?.displayName ?? productCode;
  const trimmedNumber = proposalNumber.trim();
  const proposalNoLabel = (
    proposalDetails?.quotationNo ||
    proposalDetails?.proposalNo ||
    proposalNumber ||
    ''
  ).trim();
  const isEditChrome = quoteFlow === 'edit';
  const isViewChrome = quoteFlow === 'view';

  const portalStatus = Number(proposalDetails?.statusPortal) || 0;
  const isTerminalStatus =
    portalStatus === PortalStatus.QuoteClosed ||
    portalStatus === PortalStatus.QuoteExpired ||
    portalStatus === PortalStatus.QuoteDeleted;
  const resolvedPortalStatus =
    !isTerminalStatus && isClonedQuoteMarkedAsDraft(resolvedPolicyId)
      ? PortalStatus.QuoteInProgress
      : portalStatus;
  const canClone =
    resolvedPortalStatus === PortalStatus.QuoteInProgress ||
    resolvedPortalStatus === PortalStatus.QuoteCalculated;
  const showCloneAction =
    Boolean(resolvedPolicyId) &&
    stepCode === 'CALCULATION' &&
    canWriteQuote &&
    canClone;

  const handleCloseCloneDialog = () => {
    setIsCloneDialogOpen(false);
    copyQuoteMutation.reset();
  };

  const handleConfirmClone = async () => {
    if (!resolvedPolicyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível clonar',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      handleCloseCloneDialog();
      return;
    }

    try {
      const { policyId: newPolicyId } = await copyQuoteMutation.mutateAsync({
        policyId: resolvedPolicyId,
        quoteNumber:
          proposalDetails?.quotationNo || proposalDetails?.proposalNo,
      });

      toaster.create({
        type: 'success',
        title: 'Cotação clonada com sucesso',
      });

      handleCloseCloneDialog();
      navigate({
        to: '/quotes/products/new/$id',
        params: { id: newPolicyId },
        search: {
          productId,
          category,
          productCode,
          stepCode: '',
          quoteFlow: 'edit',
        },
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Erro ao clonar cotação.';

      toaster.create({
        type: 'error',
        title: 'Não foi possível clonar',
        description: message.includes('Deductible')
          ? `${message} (edite a cotação para ajustar franquias e tente clonar novamente)`
          : message,
      });
    }
  };

  const title = isEditChrome
    ? 'Editar Cotação'
    : isViewChrome
      ? trimmedNumber
        ? `Cotação ${displayName} - Nº ${trimmedNumber}`
        : `Cotação ${displayName}`
      : trimmedNumber
        ? `Nova Cotação ${displayName} - Nº ${trimmedNumber}`
        : `Nova Cotação ${displayName}`;

  const subtitle = isEditChrome
    ? 'Edite as informações da cotação'
    : isViewChrome
      ? 'Visualize as informações da cotação'
      : 'Preencha as informações para calcular o seguro';

  const breadcrumbItems =
    isEditChrome || isViewChrome
      ? [
          {
            label: 'Página Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Cotações', href: '/quotes' },
          ...(proposalNoLabel
            ? [{ label: proposalNoLabel, active: true }]
            : [{ label: 'Cotação', active: true }]),
        ]
      : [
          {
            label: 'Página Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Cotações', href: '/quotes/products' },
          { label: displayName, active: true },
        ];

  return (
    <div className='flex flex-col gap-4 pt-0 pb-0 px-4 max-w-5xl lg:max-w-7xl mx-auto'>
      <Page.Breadcrumb items={breadcrumbItems} />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>{title}</PageHeader.Title>
            <PageHeader.Subtitle>{subtitle}</PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          {showCloneAction ? (
            <PageHeader.Actions>
              <div
                title={quoteWriteGuard.reason}
                className='flex flex-col items-end gap-1'
              >
                <Button
                  variant='ghost'
                  size='large'
                  loading={copyQuoteMutation.isPending}
                  disabled={
                    copyQuoteMutation.isPending || quoteWriteGuard.blocked
                  }
                  onClick={() => setIsCloneDialogOpen(true)}
                >
                  <Copy className='h-5 w-5' strokeWidth={1.6} />
                  Clonar
                </Button>
              </div>
            </PageHeader.Actions>
          ) : null}
        </PageHeader.Main>
      </PageHeader.Root>

      <BrokerInactiveNotice guards={[quoteWriteGuard]} />

      <Page.Content className='py-4'>
        <NewCapitalGlobalForm
          productId={productId}
          category={category}
          productCode={productCode}
          stepCode={stepCode}
          quoteFlow={quoteFlow}
        />
      </Page.Content>

      <CloneQuoteDialog
        isOpen={isCloneDialogOpen}
        isLoading={copyQuoteMutation.isPending}
        onClose={handleCloseCloneDialog}
        onConfirm={handleConfirmClone}
      />
    </div>
  );
}
